import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

const t = buildTranslate('recovery_ai.wizard.stepper')

// R12 collapses the wizard into a 4-stage "sandbox-first" flow. The legacy
// config steps (identity / philosophy / brand-voice / limits / activation) are
// no longer top-level stages — their config moves to the WP2 tuning modals over
// the Testing simulator. Testing + Locations are reserved here but render
// placeholders (the live simulator and pilot-location data are later waves).
export const STEPS = [
  { slug: 'train', labelKey: 'train_your_agent' },
  { slug: 'testing', labelKey: 'testing' },
  { slug: 'locations', labelKey: 'locations' },
  { slug: 'escalations', labelKey: 'escalations' },
] as const

export type StepSlug = typeof STEPS[number]['slug']

// Build a wizard step route from a typed slug so a future slug rename surfaces
// as a TS error at every call site instead of silently breaking deep links.
export const buildConfigurePath = (slug: StepSlug): string =>
  `/recovery_ai/configure/${slug}`

type Props = {
  currentStep: string
}

const WizardStepper = ({ currentStep }: Props) => {
  const [css] = useStyletron()
  const currentIndex = STEPS.findIndex(s => s.slug === currentStep)

  return (
    <div className={`d-flex align-items-center ${css({ gap: '24px' })}`}>
      {STEPS.map((step, index) => {
        // A stage is "reached" once it is the current stage OR an earlier one,
        // so the dot fills + checks for the stage you're on (not just the ones
        // behind it). `< currentIndex` left the active stage un-highlighted.
        const isReached = index <= currentIndex

        return (
          <div
            key={step.slug}
            className={`d-flex align-items-center ${css({ gap: '8px' })}`}
          >
            <div
              className={css({
                width: '16px',
                height: '16px',
                borderRadius: '8px',
                border: isReached ? 'none' : '2px solid rgba(255,255,255,0.6)',
                backgroundColor: isReached ? 'white' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              })}
            >
              {isReached && (
                <i
                  className={`fa fa-check ${css({
                    fontSize: '8px',
                    color: merchantTheme.colors.spearmint50,
                  })}`}
                  aria-hidden="true"
                />
              )}
            </div>
            <span
              className={css({
                fontFamily: 'Lato, sans-serif',
                fontWeight: 700,
                fontSize: '10px',
                lineHeight: '12px',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: 'white',
                whiteSpace: 'nowrap',
              })}
            >
              {t(step.labelKey)}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export default WizardStepper
